import {use} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useEnvironments} from './use-environments'
import {SessionContext} from '../contexts/SessionContext'
import type {Environment} from '../types/environment'
import type {Session} from '../types/session'

export interface AhpSessionEligibility {
  /**
   * True when the active session is bound to an MC environment that speaks
   * AHP and is currently reachable. Components that render or steer over the
   * relay should branch on this rather than on the underlying environment
   * fields directly.
   */
  eligible: boolean
  /** The environment id the session is bound to, when one is known. */
  environmentId: string | undefined
  /** The resolved environment record, when the environments query has populated. */
  environment: Environment | undefined
  /** True while the environments list is still loading; eligibility may flip when it resolves. */
  loading: boolean
}

/**
 * Decides whether the active session should be treated as AHP-backed (i.e. all
 * IO flows over the AHP relay rather than the legacy REST + Alive path).
 *
 * The decision combines three pieces of state — `environment_id` on the
 * session, the environment's `capabilities.ahp_version`, and a live
 * `status === 'online'` — gated by the environments feature flag (the same
 * flag that gates the environments query itself).
 *
 * Lifting this into a single hook means call sites (composer routing, log
 * renderer choice, repo-write-permission gating, etc.) all branch on the same
 * notion of "is AHP", so the various surfaces stay consistent.
 */
export function useIsAhpSession(session: Session | null | undefined): AhpSessionEligibility {
  const environmentId = session?.environment_id
  const environmentsFlagOn = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const {data: environments, isLoading} = useEnvironments({
    enabled: environmentsFlagOn && !!environmentId,
  })
  const environment = environmentId ? environments?.find(e => e.id === environmentId) : undefined
  const eligible =
    environmentsFlagOn && !!environmentId && !!environment?.capabilities?.ahp_version && environment.status === 'online'
  return {
    eligible,
    environmentId,
    environment,
    loading: environmentsFlagOn && !!environmentId && isLoading,
  }
}

/**
 * Convenience hook for callers that already use {@link SessionContext}.
 * Equivalent to `useIsAhpSession(use(SessionContext)?.session ?? null)`.
 */
export function useIsAhpSessionFromContext(): AhpSessionEligibility {
  const ctx = use(SessionContext)
  return useIsAhpSession(ctx?.session ?? null)
}
